import { useMemo } from 'react';
import { Howl } from 'howler';
import CONSTANTS from '../constants';

interface GameConfig {
  BASIC_SOUND?: string;
  POWER_UP_AUDIO?: string;
}

interface AudioSounds {
  basicSound: Howl;
  superPowerUpSound: Howl;
  wrongSound: Howl;
}

interface UseGameAudioReturn {
  playSound: () => void;
  playWrongSound: () => void;
  playSuperPowerUpSound: () => void;
  audioSounds: AudioSounds;
}

/**
 * Custom hook for managing game audio effects
 * @param config - Game configuration object
 * @param soundMuted - Whether sound effects are muted
 * @returns Audio controls and play functions
 */
// Default basic sound fallback (use wrong_sound as fallback since there's no default basic sound)
const DEFAULT_BASIC_SOUND = '/audio/mfersbeheaded/wrong_sound.mp3';

export const useGameAudio = (config: GameConfig, soundMuted: boolean): UseGameAudioReturn => {
  const audioSounds = useMemo(() => {
    const basicSound = new Howl({
      src: [config.BASIC_SOUND || DEFAULT_BASIC_SOUND],
      autoplay: false,
      loop: false,
      volume: 0.5,
    });

    const superPowerUpSound = new Howl({
      src: [config.POWER_UP_AUDIO || CONSTANTS.POWER_UPS.SUPER_POWER_UP.audio],
      autoplay: false,
      loop: false,
      volume: 0.5,
    });

    const wrongSound = new Howl({
      src: [CONSTANTS.WRONG_SOUND],
      autoplay: false,
      loop: false,
      volume: 0.5,
    });

    return {
      basicSound,
      superPowerUpSound,
      wrongSound,
    };
  }, [config.BASIC_SOUND, config.POWER_UP_AUDIO]);

  const playSound = (): void => {
    if (!soundMuted) {
      audioSounds.basicSound.play();
    }
  };

  const playWrongSound = (): void => {
    if (!soundMuted) {
      audioSounds.wrongSound.play();
    }
  };

  const playSuperPowerUpSound = (): void => {
    if (!soundMuted) {
      audioSounds.superPowerUpSound.play();
    }
  };

  return {
    playSound,
    playWrongSound,
    playSuperPowerUpSound,
    audioSounds,
  };
};

export default useGameAudio;
